import type { TOC } from '@ember/component/template-only';
import { Heading, Text } from 'tio-ui/components/typography';
import Drawer from 'tio-reimagined/components/drawer';
import Chip from 'tio-reimagined/components/chip';
import { formatCurrency, formatCycleDate, formatNumericDate } from 'tio-reimagined/format';
import { eq } from 'tio-ui/utilities';
import { PhCheckCircle, PhHourglass, PhClock } from 'ember-phosphor-icons';
import type { ContributionPaymentRecord } from 'tio-employee/services/reimagined-data';

/**
 * PaymentHistoryDrawer — Slide-out panel showing per-year payment history
 *
 * Opens from the right when a yearly contribution card is clicked.
 * Lists individual payments with status icon, servicer, date, amount, and status chip.
 *
 * @example
 * ```gts
 * <PaymentHistoryDrawer
 *   @isOpen={{this.isPaymentHistoryDrawerOpen}}
 *   @onClose={{this.closePaymentHistoryDrawer}}
 *   @year="2025"
 *   @payments={{this.paymentsForSelectedYear}}
 *   @total={{1500}}
 * />
 * ```
 */

type PaymentStatus = ContributionPaymentRecord['status'];

interface PaymentHistoryDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** Selected year label */
    year: string;
    /** Payments for the selected year */
    payments: ContributionPaymentRecord[];
    /** Total contribution amount for the year */
    total: number;
  };
  Element: HTMLDivElement;
}

function statusLabel(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'in_progress':
      return 'In Progress';
    case 'pending':
      return 'Pending';
  }
}

function statusIntent(status: PaymentStatus): 'success' | 'primary' | 'default' {
  switch (status) {
    case 'sent':
      return 'success';
    case 'in_progress':
      return 'primary';
    case 'pending':
      return 'default';
  }
}

function statusIconBg(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'bg-emerald-100';
    case 'in_progress':
      return 'bg-blue-100';
    case 'pending':
      return 'bg-gray-100';
  }
}

function statusIconColor(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'text-emerald-600';
    case 'in_progress':
      return 'text-blue-600';
    case 'pending':
      return 'text-gray-400';
  }
}

const PaymentHistoryDrawer: TOC<PaymentHistoryDrawerSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <Drawer @isOpen={{@isOpen}} @onClose={{@onClose}} @size="lg" @placement="right" ...attributes>
    <:header>
      <div class="flex flex-col gap-2">
        <Heading @level={{3}} class="text-xl font-semibold text-gray-900">
          Payment History
          {{@year}}
        </Heading>
        <Text class="text-base text-gray-500 leading-relaxed">
          Your employer contributions for
          {{@year}}, totaling
          {{formatCurrency @total}}.
        </Text>
      </div>
    </:header>
    <:body>
      <div class="flex flex-col gap-3 py-2">
        {{#each @payments as |payment|}}
          <div
            class="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-4"
          >
            {{! Left: Status icon + payment details }}
            <div class="flex items-center gap-3 min-w-0 flex-1">
              <div
                class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                  {{statusIconBg payment.status}}"
              >
                {{#if (eq payment.status "sent")}}
                  <PhCheckCircle @size={{22}} class={{statusIconColor payment.status}} />
                {{else if (eq payment.status "in_progress")}}
                  <PhHourglass @size={{22}} class={{statusIconColor payment.status}} />
                {{else}}
                  <PhClock @size={{22}} class={{statusIconColor payment.status}} />
                {{/if}}
              </div>
              <div class="flex flex-col gap-0.5 min-w-0">
                <Text class="text-sm font-semibold text-gray-900 truncate">
                  {{formatCycleDate payment.benefitCycle}}
                </Text>
                <div class="flex items-center gap-1.5 text-xs text-gray-500">
                  <span>{{payment.servicer}}</span>
                  {{#if payment.paidDate}}
                    <div class="w-1 h-1 rounded-full bg-gray-400"></div>
                    <span>paid {{formatNumericDate payment.paidDate}}</span>
                  {{/if}}
                </div>
              </div>
            </div>

            {{! Right: Amount + Status chip }}
            <div class="flex flex-col items-end gap-1 shrink-0">
              <Text class="text-sm font-bold text-gray-900">
                {{formatCurrency payment.amount}}
              </Text>
              <Chip @intent={{statusIntent payment.status}} @size="sm">
                {{statusLabel payment.status}}
              </Chip>
            </div>
          </div>
        {{else}}
          <div class="py-10 flex flex-col items-center justify-center gap-2 text-center">
            <Text class="text-sm text-gray-500">No payments recorded for {{@year}}.</Text>
          </div>
        {{/each}}
      </div>
    </:body>
  </Drawer>
</template>;

export default PaymentHistoryDrawer;
